/**
 * Comparison Viewer Component
 * Side-by-side comparison of satellite images with AI analysis
 */

import React from 'react';
import { 
  FaSatellite, 
  FaCalendarAlt, 
  FaRuler, 
  FaExclamationTriangle,
  FaLeaf,
  FaInfoCircle,
  FaRobot,
  FaSpinner
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ComparisonViewer.css';

const ComparisonViewer = ({ 
  data, 
  aiAnalysis, 
  aiLoading, 
  aiError
}) => {
  // Early return if no data
  if (!data) return null;

  const { image1, image2 } = data;

  // Render satellite image
  const renderSatelliteImage = (imageData, label) => {
    if (!imageData.preview_base64) {
      // Fallback to placeholder if no image data
      return (
        <div className="image-placeholder">
          <div className="placeholder-content">
            <FaSatellite className="placeholder-icon" />
            <h3>{label}</h3>
            <p className="placeholder-date">{imageData.actual_date}</p>
            <p className="placeholder-id">Image ID: {imageData.id}</p>
            <div className="placeholder-note">
              <FaExclamationTriangle className="note-icon" />
              <span>Image preview not available</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="satellite-image-container">
        <img 
          src={imageData.preview_base64} 
          alt={`Satellite ${label}`}
          className="satellite-preview-image"
        />
      </div>
    );
  };

  return (
    <div className="comparison-viewer">
      <div className="viewer-header">
        <FaLeaf className="viewer-header-icon" />
        <div>
          <h3>Vegetation Change Analysis</h3>
          <p className="viewer-subtitle">
            Side-by-side view tracking landscape transformation over time
          </p>
        </div>
      </div>

      {/* Side by side images */}
      <div className="side-by-side-container">
        {/* Before Image */}
        <div className="image-panel before-panel">
          <div className="panel-header">
            <FaCalendarAlt className="panel-icon" />
            <div>
              <h4>Before</h4>
              <p className="image-date">{new Date(image1.actual_date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
            </div>
          </div>
          <div className="panel-content">
            {renderSatelliteImage(image1, 'Before')}
          </div>
          <div className="panel-footer">
            <div className="image-info">
              <FaSatellite className="info-icon" />
              <div>
                <span className="info-label">Satellite</span>
                <span className="info-value">{image1.satellite_id}</span>
              </div>
            </div>
            <div className="image-info">
              <FaRuler className="info-icon" />
              <div>
                <span className="info-label">Resolution</span>
                <span className="info-value">{image1.pixel_resolution}m</span>
              </div>
            </div>
          </div>
        </div>

        {/* After Image */}
        <div className="image-panel after-panel">
          <div className="panel-header">
            <FaCalendarAlt className="panel-icon" />
            <div>
              <h4>After</h4>
              <p className="image-date">{new Date(image2.actual_date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
            </div>
          </div>
          <div className="panel-content">
            {renderSatelliteImage(image2, 'After')}
          </div>
          <div className="panel-footer">
            <div className="image-info">
              <FaSatellite className="info-icon" />
              <div>
                <span className="info-label">Satellite</span>
                <span className="info-value">{image2.satellite_id}</span>
              </div>
            </div>
            <div className="image-info">
              <FaRuler className="info-icon" />
              <div>
                <span className="info-label">Resolution</span>
                <span className="info-value">{image2.pixel_resolution}m</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Analysis Section */}
      <div className="ai-analysis-section">
        <div className="ai-analysis-header">
          <FaRobot className="ai-icon" />
          <div>
            <h3>AI-Powered Change Detection Analysis</h3>
            <p className="ai-subtitle">
              Gemini AI using segmentation to identify and track specific land features, vegetation changes, and environmental impacts
            </p>
          </div>
        </div>

        {/* Loading State */}
        {aiLoading && (
          <div className="ai-loading">
            <FaSpinner className="spinner-icon" />
            <div className="loading-text">
              <h4>Analyzing Images with Gemini AI...</h4>
              <p>Identifying forest areas, cleared land, agricultural fields, and infrastructure changes</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {aiError && !aiLoading && (
          <div className="ai-error">
            <FaExclamationTriangle className="error-icon" />
            <div className="error-text">
              <h4>Analysis Error</h4>
              <p>{aiError}</p>
              <p className="error-fallback">Displaying images without AI analysis. You can still manually compare the images above.</p>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {aiAnalysis && !aiLoading && !aiError && (
          <div className="ai-analysis-results">
            {/* Detailed Analysis Text */}
            <div className="analysis-content">
              <div className="analysis-header-info">
                <FaInfoCircle className="info-icon" />
                <div>
                  <h4>Detailed Analysis Report</h4>
                  {/* {aiAnalysis.metadata && aiAnalysis.metadata.used_fallback && (
                    <p className="model-info fallback-info">
                      ℹ️ Analysis generated using fallback model: <strong>{aiAnalysis.model_used}</strong>
                    </p>
                  )}
                  {aiAnalysis.model_used && !aiAnalysis.metadata?.used_fallback && (
                    <p className="model-info primary-info">
                      ✓ Model: <strong>{aiAnalysis.model_used}</strong>
                    </p>
                  )} */}
                </div>
              </div>
              <div className="analysis-text markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 className="markdown-h1" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="markdown-h2" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="markdown-h3" {...props} />
                    ),
                    h4: ({ node, ...props }) => (
                      <h4 className="markdown-h4" {...props} />
                    ),
                    h5: ({ node, ...props }) => (
                      <h5 className="markdown-h5" {...props} />
                    ),
                    h6: ({ node, ...props }) => (
                      <h6 className="markdown-h6" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="markdown-p" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="markdown-ul" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="markdown-ol" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="markdown-li" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="markdown-strong" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="markdown-em" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote className="markdown-blockquote" {...props} />
                    ),
                    code: ({ node, inline, ...props }) => (
                      <code className={inline ? "markdown-code-inline" : "markdown-code-block"} {...props} />
                    ),
                    hr: ({ node, ...props }) => (
                      <hr className="markdown-hr" {...props} />
                    ),
                  }}
                >
                  {aiAnalysis.analysis_text}
                </ReactMarkdown>
              </div>
              
              {/* Analysis Metadata */}
              <div className="analysis-metadata">
                <div className="metadata-item">
                  <FaRobot className="metadata-icon" />
                  <span className="metadata-label">Approach:</span>
                  <span className="metadata-value">Segmentation-based</span>
                </div>
                <div className="metadata-item">
                  <FaRobot className="metadata-icon" />
                  <span className="metadata-label">Model:</span>
                  <span className="metadata-value">{aiAnalysis.model_used}</span>
                </div>
                <div className="metadata-item">
                  <FaInfoCircle className="metadata-icon" />
                  <span className="metadata-label">Analysis Date:</span>
                  <span className="metadata-value">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Default message if no analysis yet */}
        {!aiAnalysis && !aiLoading && !aiError && (
          <div className="ai-placeholder">
            <FaInfoCircle className="placeholder-icon" />
            <div className="placeholder-content">
              <h4>AI Analysis Available</h4>
              <p>
                AI-powered analysis will appear here to identify specific land features (forests, clearings, buildings) 
                and track their changes over time, detecting encroachment and environmental transformations.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonViewer;
